/*
 * BioIndex
 * Copyright (c) 2026 BioIndex™. All rights reserved.
 * Shared, read-only notification badge state. No Android push permissions.
 */
import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  collection,
  doc,
  onSnapshot,
  query,
  where,
  writeBatch,
  setDoc,
} from "firebase/firestore";
import { db } from "../firebase";
import NativePushBridge from "../notifications/NativePushBridge";

const NotificationBadgeContext = createContext(null);

export function timestampMillis(value) {
  if (!value) return 0;
  if (typeof value === "number") return value;
  if (typeof value.toMillis === "function") return value.toMillis();
  if (typeof value.seconds === "number") return value.seconds * 1000;
  return Number(value) || 0;
}

export function bogotaDayKey(value) {
  const millis = timestampMillis(value);
  if (!millis) return "";
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/Bogota",
    year: "numeric", month: "2-digit", day: "2-digit",
  }).formatToParts(new Date(millis));
  const byType = Object.fromEntries(parts.map((part) => [part.type, part.value]));
  return `${byType.year}-${byType.month}-${byType.day}`;
}

export function activityDayKey(activity) {
  return activity?.dateKey || bogotaDayKey(activity?.createdAt || activity?.updatedAt) || activity?.id;
}

export function activityTime(activity) {
  return Math.max(timestampMillis(activity?.updatedAt), timestampMillis(activity?.createdAt));
}

export function isRankingNotification(notification) {
  return notification?.type === "species_rank_passed_friend" ||
    notification?.type === "species_rank_passed_by_friend";
}

// Each day's uploads, genus cards, titles and received kudos belong to one
// personal history entry. A late kudos reaction belongs to the achievement
// date, NOT the date the friend happened to send the kudos.
export function personalActivityDayKey(notification) {
  if (!notification) return "";
  const type = String(notification.type || "");
  if (!["photos_uploaded", "genus_unlocked", "title_unlocked", "kudos_received"].includes(type)) return "";
  const explicit = String(notification.activityDateKey || notification.dateKey || "").trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(explicit)) return explicit;
  if (type === "kudos_received") {
    const key = String(notification.activityKey || "");
    const match = key.match(/_(?:photos|genus)_(\d{4}-\d{2}-\d{2})$/);
    if (match) return match[1];
  }
  return bogotaDayKey(notification.createdAt) || "";
}

export function notificationGroupKey(notification) {
  if (isRankingNotification(notification)) {
    return `rankings:${bogotaDayKey(notification.createdAt) || notification.id}`;
  }
  const day = personalActivityDayKey(notification);
  if (day) return `activity:${day}`;
  return `individual:${notification.id}`;
}

export function feedGroupKey(activity) {
  return `${activity.actorUid || activity.actorName || activity.id}:${activityDayKey(activity)}`;
}

export function NotificationBadgeProvider({ uid, enabled = true, children }) {
  const [notifications, setNotifications] = useState([]);
  const [activities, setActivities] = useState([]);
  const [feedSeenAt, setFeedSeenAt] = useState(0);
  const [blocked, setBlocked] = useState([]);
  const [blockedBy, setBlockedBy] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [feedLoaded, setFeedLoaded] = useState(false);
  const [profileLoaded, setProfileLoaded] = useState(false);
  const rankingReadInFlight = useRef(false);

  useEffect(() => {
    setNotifications([]);
    setActivities([]);
    setFeedSeenAt(0);
    setBlocked([]);
    setBlockedBy([]);
    setLoaded(false);
    setFeedLoaded(false);
    setProfileLoaded(false);
    if (!enabled || !uid) return undefined;

    // Read all documents so the badge never silently caps at the seven items
    // shown on the first page. For larger communities this can later be
    // replaced by backend-maintained unread aggregates.
    const unsubNotifications = onSnapshot(
      collection(db, "users", uid, "notifications"),
      (snapshot) => {
        setNotifications(snapshot.docs.map((item) => ({ id: item.id, ...item.data() })));
        setLoaded(true);
      },
      (error) => console.error("Failed to load BioIndex badge notifications:", error),
    );
    const unsubActivities = onSnapshot(
      collection(db, "users", uid, "communityFeed"),
      (snapshot) => {
        setActivities(snapshot.docs.map((item) => ({ id: item.id, ...item.data() })));
        setFeedLoaded(true);
      },
      (error) => console.error("Failed to load BioIndex badge feed:", error),
    );
    const unsubProfile = onSnapshot(doc(db, "profile", uid),
      (snapshot) => {
        setFeedSeenAt(timestampMillis(snapshot.data()?.communityFeedSeenAt));
        setProfileLoaded(true);
      },
      (error) => console.error("Failed to load BioIndex feed watermark:", error));
    const unsubBlocked = onSnapshot(query(collection(db, "userBlocks"), where("blockerUid", "==", uid)),
      (snapshot) => setBlocked(snapshot.docs.map((item) => item.data().blockedUid).filter(Boolean)),
      (error) => console.error("Failed to load blocked accounts for badges:", error));
    const unsubBlockedBy = onSnapshot(query(collection(db, "userBlocks"), where("blockedUid", "==", uid)),
      (snapshot) => setBlockedBy(snapshot.docs.map((item) => item.data().blockerUid).filter(Boolean)),
      (error) => console.error("Failed to load blocking accounts for badges:", error));
    return () => { unsubNotifications(); unsubActivities(); unsubProfile(); unsubBlocked(); unsubBlockedBy(); };
  }, [uid, enabled]);

  const blockedSet = useMemo(() => new Set([...blocked, ...blockedBy]), [blocked, blockedBy]);
  const unread = useMemo(() => notifications.filter((item) =>
    !item.read && (!item.actorUid || !blockedSet.has(item.actorUid))), [notifications, blockedSet]);
  const unseenFeed = useMemo(() => activities.filter((item) =>
    (!item.actorUid || !blockedSet.has(item.actorUid)) && activityTime(item) > feedSeenAt),
  [activities, feedSeenAt, blockedSet]);

  const counts = useMemo(() => {
    const personal = new Set(unread.map(notificationGroupKey)).size;
    const community = new Set(unseenFeed.map(feedGroupKey)).size;
    const rankings = new Set(unread.filter(isRankingNotification).map(notificationGroupKey)).size;
    return { personal, community, rankings, total: personal + community };
  }, [unread, unseenFeed]);

  async function markFeedSeen() {
    if (!uid || !enabled) return;
    const now = Date.now();
    setFeedSeenAt(now);
    try {
      await setDoc(doc(db, "profile", uid), { communityFeedSeenAt: now }, { merge: true });
    } catch (error) {
      console.error("Could not mark BioIndex Community Feed as seen:", error);
      // A profile snapshot will eventually restore the authoritative value.
    }
  }

  async function markRankingsSeen() {
    if (!uid || !enabled || rankingReadInFlight.current) return;
    const rankingNotifications = unread.filter(isRankingNotification);
    if (!rankingNotifications.length) return;
    rankingReadInFlight.current = true;
    try {
      for (let i = 0; i < rankingNotifications.length; i += 400) {
        const batch = writeBatch(db);
        rankingNotifications.slice(i, i + 400).forEach((item) => {
          batch.update(doc(db, "users", uid, "notifications", item.id), { read: true });
        });
        await batch.commit();
      }
    } catch (error) {
      console.error("Could not mark ranking notifications read:", error);
    } finally {
      rankingReadInFlight.current = false;
    }
  }

  // Keep action closures current even when a notification changes but a
  // grouped badge count happens to remain the same.
  const value = { ...counts, loaded, notificationRecords: notifications, markFeedSeen, markRankingsSeen };

  return (
    <NotificationBadgeContext.Provider value={value}>
      <NativePushBridge uid={uid} enabled={enabled}
        loaded={loaded && feedLoaded && profileLoaded} total={counts.total} />
      {children}
    </NotificationBadgeContext.Provider>
  );
}

export function useNotificationBadges() {
  return useContext(NotificationBadgeContext) || {
    personal: 0, community: 0, rankings: 0, total: 0,
    notificationRecords: [], loaded: false,
    markFeedSeen: async () => {}, markRankingsSeen: async () => {},
  };
}
